import React from "react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { WHY_PULSE_DIFFERENTIATORS, REFORM_ECOSYSTEM_PILLS } from "@/lib/pulseConstants";

/**
 * WhyPulseSection — the consolidated section that replaces the previous
 * `PositioningSection` (Why Pulse), `DifferentiatorsSection` (Why Clients
 * Choose Pulse), and the standalone homepage `EcosystemSection` card grid.
 *
 * It answers ONE question — "why choose Pulse?" — through exactly three
 * differentiators, followed by a single compact strip that acknowledges
 * the wider REFORM Group capabilities without recreating a full card
 * grid on the homepage.
 *
 * Both desktop and mobile render the same three items:
 *   - Desktop: three equal columns.
 *   - Mobile:  three compact stacked rows (target 130–180 px each).
 *
 * The complete section is expected to fit within roughly 700–950 px on
 * mobile (heading + 3 rows + ecosystem strip). Desktop scales up
 * naturally to sit within a single scroll page-scroll depth.
 */
export default function WhyPulseSection() {
    return (
        <section
            className="section-pad relative"
            data-testid="home-why-pulse-section"
        >
            <div className="container-editorial">
                {/* Header */}
                <Reveal>
                    <SectionEyebrow index="04" label="Why Pulse" />
                </Reveal>
                <Reveal>
                    <h2 className="display-2 text-white text-balance">
                        Built to matter
                        <br />
                        <span className="text-[#FF2A2A]">beyond the event.</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.08}>
                    <p className="body-md mt-4 max-w-2xl">
                        We start with the brand objective, shape a culturally relevant idea, and build creators, content, PR and amplification into the experience from day one.
                    </p>
                </Reveal>

                {/* Three differentiators */}
                <div
                    className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
                    data-testid="why-pulse-grid"
                >
                    {WHY_PULSE_DIFFERENTIATORS.map((d, i) => (
                        <Reveal key={d.step} delay={0.1 + i * 0.06}>
                            <article
                                data-testid={`why-pulse-item-${d.step}`}
                                className="h-full rounded-2xl border border-white/10 bg-[#0B0C10] p-5 md:p-6 relative overflow-hidden"
                            >
                                <div className="flex items-start gap-4">
                                    <span
                                        aria-hidden="true"
                                        className="font-display text-3xl md:text-4xl leading-none text-[#FF2A2A]/85 shrink-0"
                                    >
                                        {d.step}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-display text-xl md:text-2xl leading-tight text-white">
                                            {d.title}
                                        </h3>
                                        <p className="text-sm md:text-[0.95rem] text-white/75 leading-relaxed mt-2">
                                            {d.body}
                                        </p>
                                    </div>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>

                {/* Compact REFORM Group ecosystem strip — visually secondary
                    to the three differentiators. Not a card grid, not a
                    section on its own. On mobile the pills wrap naturally. */}
                <Reveal delay={0.3}>
                    <div
                        data-testid="why-pulse-ecosystem-strip"
                        className="mt-8 md:mt-10 rounded-full md:rounded-2xl border border-white/10 bg-transparent px-4 py-4 md:px-6 md:py-5"
                    >
                        <div className="flex flex-col md:flex-row md:items-center md:gap-6">
                            <div className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.28em] text-white/55 md:whitespace-nowrap">
                                Powered by the wider <span className="text-white/80">REFORM Group</span>
                            </div>
                            <ul
                                aria-label="REFORM Group capabilities"
                                className="mt-3 md:mt-0 flex flex-wrap items-center gap-2 md:gap-2.5"
                            >
                                {REFORM_ECOSYSTEM_PILLS.map((p, idx) => (
                                    <React.Fragment key={p}>
                                        <li
                                            data-testid={`ecosystem-pill-${p.toLowerCase()}`}
                                            className="font-mono text-[11px] md:text-xs uppercase tracking-[0.16em] text-white/85 px-3 py-1.5 rounded-full border border-white/15 bg-black/30"
                                        >
                                            {p}
                                        </li>
                                        {idx < REFORM_ECOSYSTEM_PILLS.length - 1 && (
                                            <span aria-hidden="true" className="hidden md:inline text-[#FF2A2A]/70 text-xs">&middot;</span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
